import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, Star, StarOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useToggleFollow } from "@/hooks/useToggleFollow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { UserCard } from "@/components/cards/UserCard";
import { NativeSelect } from "@/components/ui/native-select";
import { usePathTab } from "@/lib/usePathTab";
import { useRequireAuth } from "@/hooks/useAuth";
import { useSeo } from "@/lib/useSeo";
import { fromNowFR, formatNumber } from "@/lib/utils";

const USER_TABS = ["all", "followed"] as const;
type UserTab = (typeof USER_TABS)[number];

const TAB_LABELS: Record<UserTab, string> = {
  all: "Tous",
  followed: "Suivis",
};

export default function UsersPage() {
  useSeo({
    title: "Utilisateurs les plus actifs des forums Boursorama",
    description:
      "Annuaire des membres les plus actifs des forums Boursorama : volume de messages, ancienneté, BoursoStars et activité récente. Suivez vos intervenants favoris.",
    canonicalPath: "/users",
  });
  const [q, setQ] = useState("");
  const gate = useRequireAuth();
  // `/users/:segment` : known segments ("all", "followed") map to a filter ;
  // unknown segments are dispatched to UserDetailPage upstream.
  const [tab, setTab] = usePathTab<UserTab>("segment", "/users", "all", USER_TABS);

  // Debounce the search input (300 ms) so we don't fire one API call per
  // keystroke — same pattern as the onboarding search.
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data = [], isError, refetch } = useQuery({
    queryKey: ["users", debouncedQ],
    queryFn: () => api.users(debouncedQ),
  });
  const { data: follows = [] } = useQuery({
    queryKey: ["follows"],
    queryFn: () => api.follows(),
    retry: false,
  });
  const followedIds = useMemo(
    () =>
      new Set(
        follows
          .filter((f: any) => f.target_type === "user")
          .map((f: any) => Number(f.target_id))
      ),
    [follows]
  );

  const filtered = useMemo(() => {
    if (tab === "followed") return data.filter((u) => followedIds.has(u.id));
    return data;
  }, [data, tab, followedIds]);

  // Shared optimistic follow/unfollow (instant star, rollback on error).
  const toggleFollow = useToggleFollow("user");
  const toggle = {
    mutate: (u: { id: number; username: string }) =>
      toggleFollow.mutate({ id: u.id, label: u.username }),
    isPending: toggleFollow.isPending,
  };

  return (
    <div className="flex flex-col gap-6">
      {/* ── Header ── */}
      <header className="flex items-baseline gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold shrink-0">Utilisateurs</h1>
        <p className="hidden sm:block text-sm text-foreground-muted truncate">
          {formatNumber(data.length)} utilisateurs observés
        </p>
      </header>

      {/* ── Filters ── */}
      <Card>
        <CardContent className="p-4 flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-foreground-muted pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="username, e.lafage, C947…"
              className="pl-8"
              type="search"
            />
          </div>

          {/* Native select on phones, pill group on sm+ */}
          <NativeSelect
            className="sm:hidden"
            label="Filtre"
            value={tab}
            onValueChange={(v) => setTab(v as UserTab)}
            options={USER_TABS.map((t) => ({
              value: t,
              label: TAB_LABELS[t],
              count: t === "all" ? data.length : followedIds.size,
            }))}
          />
          <div className="hidden sm:flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-muted">Filtre</span>
            <div className="flex flex-wrap gap-2">
              {USER_TABS.map((t) => (
                <Button
                  key={t}
                  variant={tab === t ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTab(t)}
                >
                  {TAB_LABELS[t]}
                  <span className="ml-1 text-xs opacity-70">
                    ({t === "all" ? data.length : followedIds.size})
                  </span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── List ── */}
      {isError ? (
        <Card>
          <CardContent className="py-10 text-center text-sm flex flex-col items-center gap-3">
            <span className="text-foreground-muted">
              Impossible de charger les utilisateurs (erreur réseau).
            </span>
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Réessayer
            </Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-foreground-muted text-sm">
            {tab === "followed"
              ? "Vous ne suivez encore aucun utilisateur."
              : "Aucun utilisateur ne correspond à cette recherche."}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile : card list */}
          <div className="md:hidden flex flex-col gap-2">
            {filtered.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                isFollowed={followedIds.has(u.id)}
                onToggleFollow={() =>
                  gate(() => toggle.mutate({ id: u.id, username: u.username }), {
                    actionLabel: "suivre un utilisateur",
                  })
                }
              />
            ))}
          </div>

          {/* Desktop : table */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[10rem]">Utilisateur</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead className="text-right">Abonnés</TableHead>
                  <TableHead className="text-right">Vu pour la dernière fois</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => {
                  const isFollowed = followedIds.has(u.id);
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        <Link
                          to={`/users/${u.id}`}
                          className="font-medium hover:text-primary"
                        >
                          {u.username}
                        </Link>
                        {u.slug && u.slug !== u.username && (
                          <span className="text-xs text-foreground-subtle ml-2">
                            {u.slug}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(u.posts_count)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(u.followers_count ?? 0)}
                      </TableCell>
                      <TableCell className="text-right text-xs text-foreground-muted">
                        {fromNowFR(u.last_seen_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant={isFollowed ? "default" : "outline"}
                          onClick={() =>
                            gate(
                              () => toggle.mutate({ id: u.id, username: u.username }),
                              { actionLabel: "suivre un utilisateur" }
                            )
                          }
                          aria-label={isFollowed ? "Ne plus suivre" : "Suivre"}
                        >
                          {isFollowed ? (
                            <Star className="size-3.5" />
                          ) : (
                            <StarOff className="size-3.5" />
                          )}
                          {isFollowed ? "Suivi" : "Suivre"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
